/**
 * The full-frame gate-opening overlay — `docs/prototypes/gate-open-cloud-at-gate-themes.html`'s
 * direction F ("cloud at the gate"), ported onto the shipped Home.
 *
 * **It reports the delivery receipt and nothing more (D134/D140).** The leaves swing on
 * `outcome: 'opened'`, which is the controller acknowledging the command in ~300 ms — not the
 * leaf finishing its travel, which takes seconds and which nothing here witnesses. That is why
 * there is no "gate is open" copy anywhere in this file, and why the whole overlay is
 * `aria-hidden`: the tile underneath is the thing that speaks, and it says *sent*.
 *
 * **Three things about the drawing, all of them the prototype's own findings:**
 *
 *  - **The leaf has no fill between its pickets.** The app shows through the gate, which is what
 *    keeps this a dim-and-blur over the running app rather than an object pasted on top of one.
 *    That leaves the ironwork as the whole of the drawing, so `--fx-iron` is the whole of the
 *    question — see the stylesheet.
 *  - **The defs are JSX, and that closes a defect the prototype shipped once.** It built the
 *    `<filter>` elements from a string and inserted them with `createContextualFragment`, which
 *    parses in *HTML* context — `<filter>` is not an HTML element, so the fragment came back as
 *    unknown nodes named `FEDROPSHADOW` carrying no `flood-color`, and `url(#runglow)` drew a
 *    black shadow where the blue glow should be. Nothing warned. React creates SVG children in
 *    the SVG namespace, so the trap cannot reach this file — but it is the reason these are
 *    elements here rather than a template string.
 *  - **The glows are SVG `<filter>`s referenced by attribute, never CSS `filter`.** Safari parses
 *    a CSS filter on an SVG element and then never paints it: computed style reports the
 *    declaration live and nothing renders.
 *
 * **The one place this does not reproduce the prototype is the wordmark's face.** That file
 * CDN-links Michroma; D78/D119 leave Michroma undelivered, and a second face means a licensed,
 * pinned `.woff2` beside Heebo plus a `flows.md` update — not a `<link>`. The wordmark is set in
 * the product's own `--font-ui` instead, tracked out to carry the width Michroma was doing.
 */
import { useEffect, useRef, useState } from 'react'
import { WordmarkPaths } from '../../components/Wordmark'
import { WORDMARK_BOX } from '../../components/wordmarkGeometry'

/**
 * What the open has answered. `pending` is the wait; the other two are terminal and each starts
 * its own exit.
 */
export type GateOpenOutcome = 'pending' | 'opened' | 'refused'

/**
 * The floor under the wait beat, measured from mount. The band travelling the leaves' outline is
 * the whole of the in-flight state, and a dev-transport open that answers in 80 ms would fade the
 * overlay in and swing the leaves inside its own 220 ms entrance — a flash, not a beat. It delays
 * the overlay's answer only; the tile underneath changes at the real time, because a floor on a
 * decoration must never become a floor on a report.
 */
const MIN_WAIT_MS = 600
/** How long the answer is held before the exit starts — long enough to read, per the prototype. */
const ANSWER_HOLD_MS = 1900
/** `.fx.out`'s own fade, which the unmount has to outlast or the overlay disappears mid-fade. */
const FADE_OUT_MS = 340

/* ====================================================================== *
 * The gate.
 *
 * One leaf, drawn from the reference icon: the top rail is a smoothstep from 27 at the post to 6
 * at the centre — a swan neck rather than a plain arc — and the same function gives every picket
 * its own top, so each ends under the rail wherever the rail happens to be.
 * ====================================================================== */
const LEAF_W = 112
const LEAF_H = 96
const SILL = 76
const LEAF_FRAME_W = 7
/**
 * The frame is centred on the leaf's top edge, so its inner lip sits half a stroke below that
 * edge; a picket stopping short of the lip leaves a hairline of the app between it and the rail,
 * which at the 1.36 the gate is scaled to reads as pickets floating under an arch rather than
 * hanging from it. The half unit past the lip is overlap — the frame is drawn after the pickets
 * and covers it, which is cheaper than trusting two antialiased edges to meet.
 */
const RAIL = LEAF_FRAME_W / 2 - 0.5

const smoothstep = (t: number): number => t * t * (3 - 2 * t)
const topAt = (x: number): number => 27 - 21 * smoothstep(x / LEAF_W)

const LEAF_EDGE = Array.from({ length: 25 }, (_, index) => {
  const x = (index / 24) * LEAF_W
  return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${topAt(x).toFixed(1)}`
}).join(' ')
/** One closed outline, which is what the travelling band needs: `pathLength="1"` on this. */
const LEAF_SHAPE = `${LEAF_EDGE} L ${LEAF_W} ${LEAF_H} L 0 ${LEAF_H} Z`

/**
 * Flat-topped pickets that run up INTO the rail rather than stopping under it. The top is
 * measured at `x + 4` — the picket's right edge — and that is the whole trick: `topAt()` falls as
 * `x` rises, so the right edge is where the arch sits highest and the frame's inner lip is
 * nearest. Measure at the centre and the right corner clears the lip by a fraction of a unit,
 * which paints a hairline between picket and rail.
 */
const PICKETS = [12, 26, 40, 54, 68, 82, 96].map((x) => {
  const top = Number((topAt(x + 4) + RAIL).toFixed(1))
  return { x, y: top, height: Number((SILL - top).toFixed(1)) }
})

/**
 * How far the cloud sits above the gate, in the scene's own 760-tall viewBox. `0` restores the
 * position it was drawn at, where the cloud bottomed out around y 196 and the gate stood up at
 * y 388 — 192 units of empty screen between two objects meant to read as one scene. Arithmetic on
 * the coordinates rather than a transform on a wrapping `<g>`, because the wordmark has to travel
 * with the cloud and a group transform is exactly the kind of thing a later edit moves only half
 * of. Past ~150 it pushes the wordmark into the leaves.
 */
const CLOUD_DROP = 96
const dropped = (n: number): number => n + CLOUD_DROP

/**
 * One closed outline. Every arc is a wide, near-circular bump, the underside included: the bottom
 * is an arc rather than a flat base, which is what keeps it round rather than cartoon-shelf-shaped.
 */
const CLOUD_PATH = `M 108 ${dropped(168)}
                 A 40 40 0 0 1 140 ${dropped(104)}
                 A 58 58 0 0 1 246 ${dropped(108)}
                 A 38 38 0 0 1 284 ${dropped(172)}
                 A 160 160 0 0 1 108 ${dropped(168)} Z`

/**
 * **The cloud's lettering, placed — D201 (g) made it the drawn wordmark, so this is arithmetic
 * rather than a font size.**
 *
 * It replaced a `<text>` at `x 196, y dropped(148)`, 24px Oxanium 300 tracked to `0.07em`, whose
 * ink measured **107.77 wide by 17.94 tall** in this viewBox with a cap height of 16.56 (read off
 * a canvas `TextMetrics`, not off `getBBox`, which reports the em box and would have said 24).
 * Those three numbers are what this placement answers to.
 *
 * **Width is what is matched, not cap height, and the cloud is why.** Matching the cap height
 * needs `16.56 / 53.81 = 0.308`, which makes the mark 118.8 wide — 10 % more than the lettering
 * the cloud was drawn around, on a shape whose usable span narrows fast either side of centre.
 * Matching the width instead costs 9 % of letter height on a mark that is already wider-set and
 * heavier than Oxanium 300, so it reads no smaller. **The drawn mark is taller than the text was
 * whichever is matched** — it has arcs above the ring and a descending `G` that Oxanium's has not
 * — which is the real constraint here and the reason this is centred on the old ink's centre
 * rather than sat on its baseline: a shared baseline would hang the new tail 7 units below where
 * anything used to be, into the cloud's lower arc.
 *
 * `CLOUD_WORDMARK_CY` is that centre — `dropped(148) - 17.76 / 2`, the old baseline lifted by half
 * its own ascent, since the text had no descender to speak of (0.18).
 */
const CLOUD_WORDMARK_W = 107.77
const CLOUD_WORDMARK_CX = 196
const CLOUD_WORDMARK_CY = dropped(148) - 17.76 / 2
const CLOUD_WORDMARK_SCALE = CLOUD_WORDMARK_W / WORDMARK_BOX.width

/**
 * The mark's own box centre, which the transform below lands on
 * (`CLOUD_WORDMARK_CX`, `CLOUD_WORDMARK_CY`). Written out rather than assumed: the x half comes to
 * 196.415, which is within half a unit of the scene's own centre by coincidence, and an edit that
 * "simplified" it away would be right until the artwork moved.
 */
const WORDMARK_CENTRE = {
  x: WORDMARK_BOX.x + WORDMARK_BOX.width / 2,
  y: WORDMARK_BOX.y + WORDMARK_BOX.height / 2,
}

/**
 * `translate(...) scale(...)`, in that order: a point `p` in the mark's coordinates lands at
 * `t + s * p`, so `t` is the target centre minus the scaled source centre. Rounded to four places
 * because this is a fixed attribute string and an irrational tail in the DOM helps nobody.
 */
const CLOUD_WORDMARK_TRANSFORM = [
  `translate(${(CLOUD_WORDMARK_CX - CLOUD_WORDMARK_SCALE * WORDMARK_CENTRE.x).toFixed(4)}`,
  `${(CLOUD_WORDMARK_CY - CLOUD_WORDMARK_SCALE * WORDMARK_CENTRE.y).toFixed(4)})`,
  `scale(${CLOUD_WORDMARK_SCALE.toFixed(6)})`,
].join(' ')

/**
 * `prefers-reduced-motion: reduce` is honoured by **not mounting this at all**, and the reason is
 * that `global.css` already carries a blanket rule zeroing every animation and transition
 * duration. Under it the overlay would not calm down — it would arrive, swing and leave inside
 * one frame, which is a full-screen flash rather than a reduced animation. The tile's own
 * `שולח…` → `נפתח` is the feedback in that case, exactly as it is today.
 *
 * A host with no `matchMedia` (a test renderer, an embedded webview) is treated as *not* reduced,
 * matching `useInstallPrompt`'s posture: absence of the query is not a preference.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function Leaf(): JSX.Element {
  return (
    <svg viewBox={`0 0 ${LEAF_W} ${LEAF_H}`} preserveAspectRatio="none" aria-hidden="true">
      {/* `iron` and `frame` are classes rather than `fill`/`stroke` attributes, which is what
          makes the material themeable at all — a presentation attribute is the lowest-priority
          half of the cascade and a per-scheme rule outranks it. */}
      <g className="iron">
        {PICKETS.map((picket) => (
          <rect key={picket.x} x={picket.x} y={picket.y} width={4} height={picket.height} />
        ))}
        <rect x={0} y={SILL} width={LEAF_W} height={10} />
      </g>
      <path className="frame" d={LEAF_SHAPE} strokeWidth={LEAF_FRAME_W} strokeLinejoin="round" />
      {/* **Two bands per leaf, one per scheme, switched in CSS — not one band whose filter is
          chosen in JS.** The two `<filter>`s differ in their `stdDeviation`s (the glow reads as a
          smudge over graphite at the light radii), and `stdDeviation` is an SVG attribute with no
          CSS property behind it, so a single element cannot carry both. The alternative is reading
          the scheme in this component, which would make a second reader of it. A hidden duplicate
          is the cheaper price, and it degrades to the light band before the document is stamped,
          because the dark copy is hidden in the base rule and revealed only by the selector.

          **D188 moved the switch from a media query to `[data-scheme="dark"]` and the argument is
          unchanged** — if anything it is stronger, since the app now has exactly one reader of the
          platform query (`theme/colorScheme.ts`) and a `matchMedia` call here would be the second. */}
      {(['light', 'dark'] as const).map((scheme) => (
        <path
          key={scheme}
          className={`leafrun leafrun-${scheme}`}
          d={LEAF_SHAPE}
          pathLength={1}
          filter={`url(#sg-runglow${scheme === 'dark' ? '-dark' : ''})`}
          strokeDasharray="0.42 0.58"
          strokeDashoffset={1}
        >
          <animate attributeName="stroke-dashoffset" from="1" to="0" dur="2.1s" repeatCount="indefinite" />
        </path>
      ))}
    </svg>
  )
}

/**
 * The gradient and the three filters. Scoped `sg-` because they are document-wide ids the moment
 * they mount, and rendered inside the overlay rather than in the shell so their CSS handles
 * inherit the per-scheme values stated on `.fx.reveal` — which is also why `HomeScreen` mounts at
 * most one overlay at a time (see its `overlay` state): two would be two of every id.
 */
function Defs(): JSX.Element {
  return (
    <svg className="fx-defs" width="0" height="0" aria-hidden="true" focusable="false">
      <defs>
        {/* `objectBoundingBox`, not `userSpaceOnUse`: two leaves share this one gradient and each
            has its own 112x96 space, so a gradient authored in one leaf's coordinates lands wrong
            on the other. The right leaf is mirrored by CSS, so the band sweeps outward from the
            centre on both rather than chasing itself across the pair. */}
        <linearGradient id="sg-gaterun" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8fd0ff" stopOpacity={0.12} />
          <stop offset="28%" stopColor="#5aaef2" stopOpacity={0.8} />
          <stop offset="52%" stopColor="#2f88e4" stopOpacity={0.95} />
          <stop offset="78%" stopColor="#1f6fd6" stopOpacity={0.45} />
          <stop offset="100%" stopColor="#14509f" stopOpacity={0.1} />
        </linearGradient>

        {/* **One cloud filter for both schemes**, unlike the band above: the prototype's two
            differ only in their `flood-opacity`s, which *is* a CSS property, so the pair collapses
            into three handles the dark block re-weights. The attributes below carry the LIGHT
            values, and that is deliberate — they are what renders on an engine that declines the
            CSS property, and the light weights in both schemes is a degradation rather than a
            break. Keep the two in step; the attribute is the one that is not allowed to drift.

            White-then-dark, because primitives chain the way CSS filter functions do: the cast
            shadow is thrown by the haloed shape and sits under the whole thing. Dark first would
            halo the shadow too and turn a glow into a grey smudge with a white rim. The halo is
            pure white, deliberately NOT the cream the cloud is filled with — cream is a surface
            colour, and a halo in it reads as a second, blurrier cloud behind the first. */}
        <filter id="sg-cloudglow" x="-60%" y="-60%" width="220%" height="220%">
          <feDropShadow
            className="tight"
            dx={0}
            dy={0}
            stdDeviation={7}
            floodColor="#ffffff"
            floodOpacity={1}
          />
          <feDropShadow
            className="wide"
            dx={0}
            dy={0}
            stdDeviation={16.8}
            floodColor="#ffffff"
            floodOpacity={0.85}
          />
          <feDropShadow
            className="cast"
            dx={0}
            dy={6}
            stdDeviation={8}
            floodColor="#2b1d16"
            floodOpacity={0.26}
          />
        </filter>

        {/* `drop-shadow()`'s third length is a blur RADIUS and `feDropShadow`'s `stdDeviation` is a
            standard deviation, so every radius halves. `blur()` is the odd one out — its argument
            already IS the standard deviation, so it carries straight over. */}
        <filter id="sg-runglow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur className="soften" stdDeviation={1.8} />
          <feDropShadow
            className="tight"
            dx={0}
            dy={0}
            stdDeviation={5}
            floodColor="#4aa3f0"
            floodOpacity={0.75}
          />
          <feDropShadow
            className="wide"
            dx={0}
            dy={0}
            stdDeviation={11}
            floodColor="#4aa3f0"
            floodOpacity={0.45}
          />
        </filter>

        {/* Graphite's band. The soften pass drops from 1.8 to .5 and both glow radii come in,
            because over a dark ground the halo was most of what was on screen and the travelling
            core was the faint part. **Reaching for a wider glow to make it more visible is the
            move that made it worse.** The gradient, the dasharray, the lap and the flood colour
            are the same; only the spread is not. */}
        <filter id="sg-runglow-dark" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur className="soften" stdDeviation={0.5} />
          <feDropShadow
            className="tight"
            dx={0}
            dy={0}
            stdDeviation={2.6}
            floodColor="#4aa3f0"
            floodOpacity={0.42}
          />
          <feDropShadow
            className="wide"
            dx={0}
            dy={0}
            stdDeviation={6}
            floodColor="#4aa3f0"
            floodOpacity={0.25}
          />
        </filter>
      </defs>
    </svg>
  )
}

export interface GateOpenOverlayProps {
  /** The open's own state. `pending` holds the wait; `opened`/`refused` each start an exit. */
  readonly outcome: GateOpenOutcome
  /** Called once the exit has finished. The caller unmounts on it — nothing self-unmounts. */
  readonly onDone: () => void
}

export function GateOpenOverlay({ outcome, onDone }: GateOpenOverlayProps): JSX.Element {
  const [answered, setAnswered] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const mountedAt = useRef(Date.now())
  const timers = useRef<number[]>([])
  /**
   * `onDone` is a fresh closure on every `HomeScreen` render, and this effect starts timers —
   * depending on its identity would restart the whole exit choreography every time the tile
   * underneath changed state, which is exactly when it changes.
   */
  const done = useRef(onDone)
  done.current = onDone

  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const id of pending) window.clearTimeout(id)
    }
  }, [])

  useEffect(() => {
    if (outcome === 'pending') return
    const after = (ms: number, run: () => void): void => {
      timers.current.push(window.setTimeout(run, ms))
    }
    if (outcome === 'refused') {
      // The chosen behaviour for every refusal — denied, `gate-busy`, `not-authorized`, offline:
      // the overlay leaves without an answer beat and the tile's own denied state carries the
      // message. A full-screen refusal surface is something no PRD specifies and the prototype
      // explicitly does not answer.
      setLeaving(true)
      after(FADE_OUT_MS, () => done.current())
      return
    }
    const wait = Math.max(0, MIN_WAIT_MS - (Date.now() - mountedAt.current))
    after(wait, () => setAnswered(true))
    after(wait + ANSWER_HOLD_MS, () => setLeaving(true))
    after(wait + ANSWER_HOLD_MS + FADE_OUT_MS, () => done.current())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  return (
    <div className={`fx reveal${answered ? ' open' : ''}${leaving ? ' out' : ''}`} aria-hidden="true">
      <Defs />
      <div className="scrim" />
      {/* `preserveAspectRatio="none"`: every object's position is a fraction of the frame, so the
          cloud stays the same distance above a gate that is itself placed at 62% whatever the
          frame's aspect. The cost is that the cloud stretches with an unusually tall frame — the
          composition is what is being preserved, not the circle. */}
      <svg className="scene" viewBox="0 0 390 760" preserveAspectRatio="none" aria-hidden="true">
        <path className="cloud" d={CLOUD_PATH} filter="url(#sg-cloudglow)" />
        <path className="cloudedge" d={CLOUD_PATH} />
        {/* `aria-hidden` is inherited from the overlay root, so this needs no accessible name —
            unlike the bar's `Wordmark`, whose `<title>` is a real one. The tile behind this is
            what reports the open (D134/D140); the overlay carries no words of its own, and a
            drawing of the product's name is not a word. */}
        <g className="cloud-wordmark" transform={CLOUD_WORDMARK_TRANSFORM}>
          <WordmarkPaths />
        </g>
      </svg>
      <div className="bloom" />
      <div className="ring" />
      <div className="ring b" />
      <div className="gate">
        <span className="post l">
          <i />
        </span>
        <span className="post r">
          <i />
        </span>
        <span className="leaf l">
          <Leaf />
        </span>
        <span className="leaf r">
          <Leaf />
        </span>
      </div>
    </div>
  )
}
